import { useMemo } from "react";
import { useSelector } from "react-redux";
import { helpers, format } from "../../../../common/lib";

/**
 * useUnexpectedAssetHistory
 * Builds structured history data for an unexpected asset (alt asset).
 */
export default function useUnexpectedAssetHistory(data) {
    const { accounts, typesList, altAsset } = useSelector((state) => state);

    /**
     * Guards on a real field rather than on truthiness, so a missing account
     * cannot render as "undefined undefined".
     */
    const getUser = (account, actorId) => ({
        name: account?.firstName
            ? `${account.firstName} ${account.surname || ""}`.trim()
            : "",
        nameKey:
            !account?.firstName && actorId != null ? "userNotFound" : null,
        avatarPath: account?.avatarPath || ""
    });

    const buildEntry = ({ user, date, heading, extra = {} }) => ({
        user,
        date: date || "",
        heading,
        ...extra
    });

    const getInfoDetail = (item, type) => {
        const user = getUser(item.user, item.userId);

        switch (type) {
            case "resolved":
                return buildEntry({
                    user,
                    date: item.date,
                    heading: "assetResolved",
                    extra: {
                        systemActorKey: item.isSystemDecision
                            ? "autoResolvedByCheckVentory"
                            : null,
                        resolutionReason: item.resolutionReason
                    }
                });

            case "image":
                return buildEntry({
                    user,
                    date: item.date,
                    heading: "addedNewImage",
                    extra: {
                        imageType: item.imageType
                    }
                });

            default:
                return null;
        }
    };

    const history = useMemo(() => {
        if (!data) return [];

        const assetHistory = [];

        // 1. Add resolved entries
        if (data.isResolved && Array.isArray(data.altAssetResolutions)) {
            data.altAssetResolutions.forEach((res) => {
                const user = accounts.data[accounts.byId[res.decisionUser]];
                const reason = helpers.getListItemForId(
                    typesList.altAssetResolutionTypes,
                    res.resolutionReason
                );

                const entry = getInfoDetail(
                    {
                        user,
                        userId: res.decisionUser,
                        isSystemDecision: !res.decisionUser,
                        date: format.dateTimeNew(res.decisionDate),
                        resolutionReason: reason?.name
                    },
                    "resolved"
                );

                if (entry) assetHistory.push(entry);
            });
        }

        // 2. Add image entries
        if (Array.isArray(data.auditImages)) {
            data.auditImages.forEach((img) => {
                const imageType = helpers.getListItemForId(
                    typesList.imageTypes,
                    img.imageType
                );

                const entry = getInfoDetail(
                    {
                        user: accounts.data[accounts.byId[data.createdBy]],
                        userId: data.createdBy,
                        date: format.dateTimeNew(img.createdDate),
                        imageType: imageType?.name || "Unknown"
                    },
                    "image"
                );

                if (entry) assetHistory.push(entry);
            });
        }

        // 3. Add note entries
        if (Array.isArray(data.notes)) {
            data.notes.forEach((item) => {
                const user = accounts.data[accounts.byId[item.createdBy]];
                const entry = buildEntry({
                    user: getUser(user, item.createdBy),
                    date: format.dateTimeNew(item.createdDate),
                    heading: "assetNote",
                    extra: {
                        note: item.note
                    }
                });

                if (entry) assetHistory.push(entry);
            });
        }

        return [...assetHistory].sort((a, b) => {
            const priority = (entry) =>
                entry.heading === "assetResolved" ? 0 : 1;

            const pA = priority(a);
            const pB = priority(b);

            if (pA !== pB) return pA - pB;
            return new Date(b.date) - new Date(a.date); // newest first
        });
    }, [data, data?.altAssetResolutions, altAsset, accounts, typesList]);

    return history;
}
